<script setup lang="ts">
import { computed, inject } from "vue";

import { navigationActions } from "@/action/navigationActions";
import { cardMetaKey } from "@/components/StickyNote/injectKeys";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListFooter from "@/components/ui/MenuList/MenuListFooter.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import type { LinkableItem, LinkingTarget } from "@/model/link";
import { isArtLevelWorkitem, isDependency } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { useCardStore } from "@/store/card";
import { sortLinkableItems } from "@/store/helpers/linkableItemsSort";
import { useLinkStore } from "@/store/link";
import { useModalStore } from "@/store/modal";
import { useUserStore } from "@/store/user";
import { injectStrict } from "@/utils/vue";

import { isActionVisible } from "../StickyNote/internal/ActionMenu/actions";
import LinkRow from "./LinkRow.vue";
import { hideDuplicateWorkitems, shouldShowNestedLinks } from "./internal/linkableItems";

const props = defineProps<{ linkingTarget: LinkingTarget; showUpperLevelLinks?: boolean }>();
const emit = defineEmits<{ add: [] }>();

const sortedLinks = computed(() => {
  const links = useLinkStore().getLinkedItems(props.linkingTarget, true);
  const sorted = sortLinkableItems(links);
  return hideDuplicateWorkitems(sorted, props.linkingTarget);
});

const dropdown = injectStrict(dropdownKey);
const cardMeta = inject(cardMetaKey, null);
const readonly =
  props.linkingTarget.type === "sticky" && cardMeta
    ? cardMeta.isReadonly
    : !useUserStore().isAllowed("edit");

const addLinksDisabled = computed(() => readonly || useModalStore().isModalOpen());

const handleAdd = () => {
  emit("add");
  dropdown.close();
};

const showBreakdownButton = computed(() => {
  switch (props.linkingTarget.type) {
    case "objective":
      return true;
    case "sticky": {
      const card = useCardStore().cards[props.linkingTarget.id];
      const board = useBoardStore().currentBoard();
      const typeMatches = isArtLevelWorkitem(card) || isDependency(card);
      return typeMatches && isActionVisible("breakdown", card, board);
    }
    default:
      return false;
  }
});

const showBreakdownBoard = () => {
  navigationActions.showBreakdown("attribute-chip", props.linkingTarget);
  dropdown.close();
};

const showNestedLinks = (linkedItem: LinkableItem) =>
  shouldShowNestedLinks(props.linkingTarget, linkedItem);
</script>

<template>
  <MenuList
    class="linked-items"
    role="dialog"
    aria-labelledby="linked-items-title"
    @keydown.tab.stop
  >
    <MenuListHeader id="linked-items-title" v-autofocus>
      <div class="header">
        <MenuListTitle>{{ $t("general.links") }}</MenuListTitle>
        <BaseButton
          v-if="showBreakdownButton"
          color="grey"
          variant="filled"
          icon-before="board/breakdown"
          size="small"
          @click="showBreakdownBoard"
        >
          {{ $t("breakdownModal.title") }}
        </BaseButton>
      </div>
    </MenuListHeader>
    <div v-if="sortedLinks.length" class="direct-links-header">
      <span class="direct-links-label">{{ $t("linkChip.directLinks") }}</span>
      <span class="direct-links-count">{{ $t("linkChip.itemCount", sortedLinks.length) }}</span>
    </div>
    <MenuListItems class="menu-list-items" static :aria-label="$t('general.links')" role="list">
      <!-- Empty state -->
      <ListEmpty v-if="!sortedLinks.length" icon="general/link" class="empty">
        {{ $t("general.noLinkedStickies") }}
      </ListEmpty>
      <LinkRow
        v-for="(link, id) in sortedLinks"
        :key="id"
        class="link-row"
        :targets="[linkingTarget]"
        :link
        :show-upper-level-links="showUpperLevelLinks"
        :show-nested-links="showNestedLinks(link)"
      />
    </MenuListItems>
    <MenuListFooter
      :label="$t('linkChip.addLinks')"
      :disabled="addLinksDisabled"
      @click="handleAdd"
    />
  </MenuList>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

.linked-items {
  font-size: 12px;

  .menu-list-header {
    min-height: 48px;
    display: flex;
    justify-content: center;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 8px;
  }

  .menu-list-items {
    gap: 0;
    padding: 0;
  }

  .empty {
    height: 100px;
  }

  .link-row {
    padding: 8px 12px;
  }
}

.direct-links-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;

  .direct-links-label {
    @include typography.small("semi-bold");

    color: colors.$text;
  }

  .direct-links-count {
    @include typography.small("semi-bold");

    color: colors.$text-subtle;
  }
}
</style>
